'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import posthog, { captureAnalyticsEvent, isPostHogEnabled } from '@/lib/analytics/posthog';
import { useUser } from '@/store/hooks';

export default function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useUser();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastPageviewKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isPostHogEnabled() || typeof window === 'undefined') return;
    if ((posthog as unknown as { __booknaav_initialized?: boolean }).__booknaav_initialized) return;

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: '/ingest',
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST?.replace('https://us.i.posthog.com', 'https://us.posthog.com')
        .replace('https://eu.i.posthog.com', 'https://eu.posthog.com') || 'https://us.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: false,
      persistence: 'localStorage+cookie',
    });

    (posthog as unknown as { __booknaav_initialized?: boolean }).__booknaav_initialized = true;
  }, []);

  useEffect(() => {
    if (!isPostHogEnabled()) return;

    if (isAuthenticated && user?.id) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        isOperator: user.isOperator,
        isTourist: user.isTourist,
      });
      return;
    }

    posthog.reset();
  }, [isAuthenticated, user?.id, user?.email, user?.name, user?.isOperator, user?.isTourist]);

  useEffect(() => {
    if (!isPostHogEnabled()) return;

    const query = searchParams?.toString() || '';
    const pageviewKey = `${pathname}?${query}`;
    if (lastPageviewKeyRef.current === pageviewKey) {
      return;
    }
    lastPageviewKeyRef.current = pageviewKey;

    captureAnalyticsEvent('$pageview', {
      path: pathname,
      query: query || null,
      currentUrl: typeof window !== 'undefined'
        ? `${window.location.origin}${pathname}${query ? `?${query}` : ''}`
        : pathname,
    });
  }, [pathname, searchParams?.toString()]);

  return <PHProvider data-component-id="src/components/providers/PostHogProvider.tsx:68:9" data-component-path="src/components/providers/PostHogProvider.tsx" data-component-line="68" data-component-end-line="68" data-component-file="PostHogProvider.tsx" data-component-name="PHProvider" data-component-content="%7B%22elementName%22%3A%22PHProvider%22%7D" client={posthog}>{children}</PHProvider>;
}
